import React from "react";
import { TableProps } from "@amzn/awsui-components-react/polaris/table";

import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import { LAUNCH_ASIN_TYPES } from "../../../constants/launchAsinTypes";
import { serviceToDisplay } from "../../../util/dateUtils";
import { VALIDATION_ATTRIBUTE_OVERALL_RESULT } from "../validationAttribute";
import { VALIDATION_COLUMN_DEFINITIONS } from "../asinValidationTable-config";
import { parseValidationStatus } from "./parseValidationStatus";
import { ValidationStatusCell } from "./ValidationStatusCell";

const BUNDLE_ASIN_TYPES: ReadonlySet<string> = new Set([
  "soft_bundle",
  "vmp_bundle",
]);

const VALIDATION_RESULT_COLUMN_IDS: readonly string[] = [
  "validationOverallResultHattrick",
  "validationOverallResultLVS",
  "validationOverallResultManualDGL",
  "validationOverallResultManualRL",
  "validationOverallResultManualInstock",
  "validationOverallResultManualOX",
  "validationOverallResultManualGTM",
  "validationOverallResultManualCS",
];

export const DEFAULT_VISIBLE_COLUMNS: readonly string[] = [
  "asin",
  "announceDate",
  "programName",
  "marketplace",
  "description",
  "validationStatus",
  "validationOverallResultHattrick",
  "validationOverallResultLVS",
];

// Hattrick and LVS cells are left to the shared renderer: their
// label lives inside an <a> whose Polaris color wins over any
// surrounding <Box color>, so a text tint would be a visible no-op.
// The pass / fail / no-data signal for those columns comes from the
// icon the shared renderer already prepends.
const CELL_OVERRIDES: Readonly<
  Record<string, (item: FullAsinWithAudits) => React.ReactNode>
> = {
  validationStatus: (item) => (
    <ValidationStatusCell
      value={item.validationStatus as string | null | undefined}
    />
  ),
};

export function overrideCellsFor<T>(
  baseDefs: readonly TableProps.ColumnDefinition<T>[],
  overrides: Readonly<Record<string, (item: T) => React.ReactNode>>
): readonly TableProps.ColumnDefinition<T>[] {
  return baseDefs.map((def) => {
    const override = overrides[def.id ?? ""];
    return override === undefined ? def : { ...def, cell: override };
  });
}

function asinFirst<T>(
  defs: readonly TableProps.ColumnDefinition<T>[]
): readonly TableProps.ColumnDefinition<T>[] {
  const asinIndex = defs.findIndex((def) => def.id === "asin");
  if (asinIndex <= 0) {
    return defs;
  }
  const asin = defs[asinIndex];
  return [asin, ...defs.slice(0, asinIndex), ...defs.slice(asinIndex + 1)];
}

export const DASHBOARD_COLUMN_DEFINITIONS: readonly TableProps.ColumnDefinition<FullAsinWithAudits>[] =
  asinFirst(overrideCellsFor(VALIDATION_COLUMN_DEFINITIONS, CELL_OVERRIDES));

function extractDateString(item: FullAsinWithAudits, key: string): string {
  const val = (item as unknown as Record<string, unknown>)[key];
  if (val === null || val === undefined || val === "") {
    return "";
  }
  return serviceToDisplay(val as Date);
}

function extractDescription(item: FullAsinWithAudits): string {
  const val = item.description as string | null | undefined;
  if (val === null || val === undefined || val === "") {
    return "";
  }
  if (!BUNDLE_ASIN_TYPES.has(item.asinType)) {
    return val;
  }
  // The table renders bundle descriptions through formattedBundleDescription
  // which turns `*...*` Markdown italics into <em>. Strip the markers so
  // the CSV shows the same visible text as the rendered cell.
  return val.replace(/\*(.*?)\*/g, "$1");
}

function extractValidationResult(
  item: FullAsinWithAudits,
  key: string
): string {
  const val = (item as unknown as Record<string, unknown>)[key];
  if (typeof val !== "string" || val === "") {
    return "";
  }
  return VALIDATION_ATTRIBUTE_OVERALL_RESULT[val] || val;
}

function validationResultOverrides(): Record<
  string,
  (item: FullAsinWithAudits) => string
> {
  const entries: Record<string, (item: FullAsinWithAudits) => string> = {};
  for (const id of VALIDATION_RESULT_COLUMN_IDS) {
    entries[id] = (item) => extractValidationResult(item, id);
  }
  return entries;
}

// Text extractors that mirror the table's rendering for CSV export.
// A column only needs an entry here when its rendered value differs
// from the raw scalar (e.g. `validationStatus` renders as "N / M",
// date columns render in PST, `asinType` renders as its display label,
// validation result columns render icon + display label, description
// strips Markdown italics for bundle ASINs).
// Everything else falls back to the raw scalar in `ExportCsvButton`.
export const CSV_TEXT_OVERRIDES: Readonly<
  Record<string, (item: FullAsinWithAudits) => string>
> = {
  validationStatus: (item) => {
    const ratio = parseValidationStatus(
      item.validationStatus as string | null | undefined
    );
    return ratio === undefined ? "" : `${ratio.passed} / ${ratio.total}`;
  },
  announceDate: (item) => extractDateString(item, "announceDate"),
  onlineStreetDate: (item) => extractDateString(item, "onlineStreetDate"),
  asinType: (item) => {
    const val = item.asinType as string | null | undefined;
    if (val === null || val === undefined || val === "") {
      return "";
    }
    return LAUNCH_ASIN_TYPES[val] || val;
  },
  description: extractDescription,
  ...validationResultOverrides(),
};
